/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react';
import { User } from '@auth0/auth0-spa-js';
import { ITenant, IUser } from '@monetize/types/app';
import { AUTH0, KEYS } from '@monetize/ui/config';
import { useQuery } from '@tanstack/react-query';
import jwt_decode from 'jwt-decode';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { getAppStore } from '../store/appStore';
import useCurrentUser from './../hooks/useCurrentUser';
import {
  accessTokenState,
  currentTenantState,
  currentUserIdState,
  isLoggingOutState,
  refreshAccessTokenState,
} from './../store/global.store';

// eslint-disable-next-line no-restricted-imports
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useInitThirdPartyContext } from './../hooks/useInitThirdPartyContext';

interface AccessTokenUpdateResponse {
  updatedAccessToken: string;
  updatedUserId: string;
}

interface GetAndHandleAccessTokenOptions {
  /**
   * Bypass the Auth0 SDK token cache and fetch a brand new token from Auth0.
   * Only needed when the token's claims are expected to have changed (user
   * created, tenant created/deleted, invite accepted). Everything else should
   * use the cached token, which the SDK refreshes itself once it expires.
   */
  forceRefresh?: boolean;
}

const buildReturnToWithJamParams = () => {
  const fallbackReturnTo = AUTH0.returnTo || '/auth/login';
  const hasWindow = typeof window !== 'undefined';

  try {
    const baseOrigin = hasWindow ? window.location.origin : undefined;
    const returnTo = baseOrigin
      ? new URL(fallbackReturnTo, baseOrigin)
      : new URL(fallbackReturnTo);

    if (hasWindow) {
      const current = new URLSearchParams(window.location.search);
      current.forEach((value, key) => {
        if (key.startsWith('jam-')) {
          returnTo.searchParams.set(key, value);
        }
      });
    }

    return returnTo.toString();
  } catch {
    return fallbackReturnTo;
  }
};

type AuthProviderType = {
  // From auth0
  isAuthenticated: boolean;
  user: User;
  isLoading: boolean;
  isLDLoading: boolean;
  loginWithRedirect:
    | (() => void)
    | ((options?: RedirectLoginOptions | undefined) => Promise<void>);
  logoutAuth0: () => void;

  // Auth state related
  setCurrentTenant: (tenant: ITenant | null) => void;
  currentTenant?: null | ITenant;
  accessToken: string;
  tenantId: string;
  userId: string;

  // Access token related
  handleAccessToken: (token: string) => {
    updatedUserId?: string;
    updatedAccessToken: string;
  };
  getAndHandleAccessTokenSilently: (
    options?: GetAndHandleAccessTokenOptions,
  ) => Promise<AccessTokenUpdateResponse>;
  getTenantIdsFromAccessToken: (accessTokenP?: string) => string[];
  hasTenantInAccessToken: (tenantId: string) => boolean;
  getDecodedToken: (accessTokenP?: string) => any;

  selectTenant: (tenant: ITenant | null) => void;

  // currentTenantUser & permission related
  currentTenantUser: IUser;
  currentTenantUserHasRole: (roles: string[]) => boolean;
  currentTenantUserHasSpecificRole: (role: string) => boolean;
  isTokenRefreshed: boolean;
  error: any;
};

export const useAuthProvider = () => {
  const {
    logout,
    getAccessTokenSilently,
    isAuthenticated,
    loginWithRedirect,
    user,
    isLoading,
  } = useAuth0();
  const [currentTenant, setCurrentTenant] = useAtom(currentTenantState);
  const tenantId = currentTenant?.id || '';
  const [prevTenantId, setPrevTenantId] = useState(tenantId);
  const [userId, setUserId] = useAtom(currentUserIdState);
  const [accessToken, setAccessToken] = useAtom(accessTokenState);
  const setRefreshTokenFn = useSetAtom(refreshAccessTokenState);
  const isLoggingOut = useAtomValue(isLoggingOutState);
  const [isTokenRefreshed, setIsTokenRefreshed] = useState(false);
  const {
    error,
    currentTenantUser,
    currentTenantUserHasRole,
    currentTenantUserHasSpecificRole,
  } = useCurrentUser(isTokenRefreshed, tenantId, userId);
  const ldClient = useLDClient();

  // Store refresh function in store so that axios has access to call it
  const storeRefreshAccessToken = useCallback(() => {
    return getAccessTokenSilently({ cacheMode: 'off' });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    setRefreshTokenFn(() => storeRefreshAccessToken);
  }, [storeRefreshAccessToken, setRefreshTokenFn]);

  useEffect(() => {
    if (tenantId && tenantId !== prevTenantId) {
      setPrevTenantId(tenantId);
    }
  }, [prevTenantId, tenantId]);

  const isLDLoading = useInitThirdPartyContext({
    ldClient,
    currentTenantUser,
    currentTenant,
    authUser: user,
    userId,
  });

  // Load the access token on initial load. The SDK serves its cached token
  // while it is still valid and only goes to Auth0 once it has expired, so
  // this is normally a local read rather than a network round trip.
  const { data: authData } = useQuery({
    queryKey: ['auth', 'getAndHandleAccessTokenSilently', currentTenant?.id],
    queryFn: () => getAndHandleAccessTokenSilently(),
    enabled: isAuthenticated && !isLoggingOut,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (authData) {
      setIsTokenRefreshed(true);
    }
  }, [authData]);

  const handleAccessToken = (accessTokenP: string) => {
    let updatedUserId = '';

    // Read the latest value directly (not the render-time `isLoggingOut`) since
    // this can resolve after a logout began, once the token fetch was already in flight
    if (getAppStore().get(isLoggingOutState)) {
      return { updatedUserId, updatedAccessToken: accessTokenP };
    }

    setAccessToken(accessTokenP);

    const jwtDecoded: any = jwt_decode(accessTokenP);

    if (jwtDecoded[KEYS.userKey]) {
      updatedUserId = jwtDecoded[KEYS.userKey];
      setUserId(updatedUserId);
    }

    return { updatedUserId, updatedAccessToken: accessTokenP };
  };

  const selectTenant = (tenant: ITenant | null) => {
    setCurrentTenant(tenant);
  };

  const getDecodedToken = (accessTokenP?: string) => {
    try {
      const jwtDecoded: any = jwt_decode(accessTokenP || accessToken);
      return jwtDecoded;
    } catch (err) {
      return null;
    }
  };

  const getTenantIdsFromAccessToken = (accessTokenP?: string): string[] => {
    try {
      const jwtDecoded: any = jwt_decode(accessTokenP || accessToken);
      return jwtDecoded[KEYS.tenantKey] || [];
    } catch (err) {
      return [];
    }
  };

  const hasTenantInAccessToken = (tenantIdP: string) =>
    getTenantIdsFromAccessToken().includes(tenantIdP);

  const logoutAuth0 = async () => {
    setCurrentTenant(null);
    logout({ logoutParams: { returnTo: buildReturnToWithJamParams() } });
  };

  const getAndHandleAccessTokenSilently = async ({
    forceRefresh = false,
  }: GetAndHandleAccessTokenOptions = {}): Promise<AccessTokenUpdateResponse> => {
    const newToken = await getAccessTokenSilently({
      cacheMode: forceRefresh ? 'off' : 'on',
    });
    return handleAccessToken(newToken);
  };

  const state: AuthProviderType = {
    // From auth0
    isAuthenticated,
    user: user!,
    isLDLoading,
    isLoading,
    loginWithRedirect,
    logoutAuth0,

    // Auth state related
    setCurrentTenant,
    currentTenant,
    accessToken,
    tenantId,
    userId,

    // Access token related
    handleAccessToken,
    getAndHandleAccessTokenSilently,
    getTenantIdsFromAccessToken,
    hasTenantInAccessToken,
    getDecodedToken,

    selectTenant,

    // currentTenantUser & permission related
    currentTenantUser: currentTenantUser as any,
    currentTenantUserHasRole,
    currentTenantUserHasSpecificRole,
    isTokenRefreshed,
    error,
  };
  return state;
};

const defaultAuthContext = {};

export const AuthContext = createContext<AuthProviderType>(
  defaultAuthContext as any,
);

export const useAuth = () => useContext(AuthContext);
